/**
 * CIELAB (D65) conversion and CIEDE2000 color difference, used for Huesswork
 * scoring per spec §9.5. Independent of the OKLab space used for gradient
 * construction (§9.4) — this is the standard CIE space.
 */
import { rgbUnitToLinear } from "./srgb.js";
// D65 reference white, normalized to Y=1.
const XN = 0.950489;
const YN = 1.0;
const ZN = 1.08884;
function f(t) {
    const delta = 6 / 29;
    return t > delta ** 3 ? Math.cbrt(t) : t / (3 * delta * delta) + 4 / 29;
}
export function rgbUnitToXyz(c) {
    const lin = rgbUnitToLinear(c);
    return {
        x: 0.4124564 * lin.r + 0.3575761 * lin.g + 0.1804375 * lin.b,
        y: 0.2126729 * lin.r + 0.7151522 * lin.g + 0.072175 * lin.b,
        z: 0.0193339 * lin.r + 0.119192 * lin.g + 0.9503041 * lin.b,
    };
}
export function rgbUnitToLab(c) {
    const { x, y, z } = rgbUnitToXyz(c);
    const fx = f(x / XN);
    const fy = f(y / YN);
    const fz = f(z / ZN);
    return {
        L: 116 * fy - 16,
        a: 500 * (fx - fy),
        b: 200 * (fy - fz),
    };
}
/**
 * CIEDE2000 color difference (Sharma, Wu, Dalal 2005 reference implementation).
 */
export function deltaE2000(lab1, lab2) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const { L: L1, a: a1, b: b1 } = lab1;
    const { L: L2, a: a2, b: b2 } = lab2;
    const C1 = Math.sqrt(a1 * a1 + b1 * b1);
    const C2 = Math.sqrt(a2 * a2 + b2 * b2);
    const CBar = (C1 + C2) / 2;
    const C7 = Math.pow(CBar, 7);
    const pow25_7 = Math.pow(25, 7);
    const G = 0.5 * (1 - Math.sqrt(C7 / (C7 + pow25_7)));
    const a1p = (1 + G) * a1;
    const a2p = (1 + G) * a2;
    const C1p = Math.sqrt(a1p * a1p + b1 * b1);
    const C2p = Math.sqrt(a2p * a2p + b2 * b2);
    const h1p = hueAngle(b1, a1p);
    const h2p = hueAngle(b2, a2p);
    const dLp = L2 - L1;
    const dCp = C2p - C1p;
    let dhp = 0;
    if (C1p * C2p !== 0) {
        dhp = h2p - h1p;
        if (dhp > 180)
            dhp -= 360;
        else if (dhp < -180)
            dhp += 360;
    }
    const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(toRad(dhp) / 2);
    const LBarp = (L1 + L2) / 2;
    const CBarp = (C1p + C2p) / 2;
    let hBarp;
    if (C1p * C2p === 0) {
        hBarp = h1p + h2p;
    }
    else if (Math.abs(h1p - h2p) <= 180) {
        hBarp = (h1p + h2p) / 2;
    }
    else if (h1p + h2p < 360) {
        hBarp = (h1p + h2p + 360) / 2;
    }
    else {
        hBarp = (h1p + h2p - 360) / 2;
    }
    const T = 1 -
        0.17 * Math.cos(toRad(hBarp - 30)) +
        0.24 * Math.cos(toRad(2 * hBarp)) +
        0.32 * Math.cos(toRad(3 * hBarp + 6)) -
        0.2 * Math.cos(toRad(4 * hBarp - 63));
    const dTheta = 30 * Math.exp(-(((hBarp - 275) / 25) ** 2));
    const RC = 2 * Math.sqrt(Math.pow(CBarp, 7) / (Math.pow(CBarp, 7) + pow25_7));
    const RT = -Math.sin(toRad(2 * dTheta)) * RC;
    const SL = 1 + (0.015 * (LBarp - 50) ** 2) / Math.sqrt(20 + (LBarp - 50) ** 2);
    const SC = 1 + 0.045 * CBarp;
    const SH = 1 + 0.015 * CBarp * T;
    const kL = 1;
    const kC = 1;
    const kH = 1;
    const term1 = dLp / (kL * SL);
    const term2 = dCp / (kC * SC);
    const term3 = dHp / (kH * SH);
    return Math.sqrt(term1 * term1 + term2 * term2 + term3 * term3 + RT * term2 * term3);
}
function hueAngle(b, ap) {
    if (b === 0 && ap === 0)
        return 0;
    let h = (Math.atan2(b, ap) * 180) / Math.PI;
    if (h < 0)
        h += 360;
    return h;
}
//# sourceMappingURL=lab.js.map